/**
 * "Show how" guides for the starter tasks (tasks.ts TASK_SEEDS and EVENT_CHECKLIST), so a task whose one-line `how`
 * is all it has still gets real steps. Every step, interval, product, temperature, dilution and time comes from a
 * research row: `task-guides#TG12` is row TG12 of docs/source/research/task-guides.md, `barista-tools#CL3` is row CL3
 * of docs/source/research/barista-tools.md, and so on. Tasks with no source for their steps have no guide here and
 * are listed in TASK_GUIDES_NOT_SOURCED (and in that doc's "Not sourced yet" section). The test
 * (test/taskguides.test.ts) checks every citation resolves, every number appears in a cited quote, and every Easy step
 * passes lintEasy.
 */
import { warningsFirst, type EasyProcedure, type EasyStep } from "./easy.ts";
import { TASKGUIDES_UT } from "./states/ut/taskguides.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

export interface TaskGuide {
  taskKey: string;
  /** Plain steps, one action each, 10 at most. */
  steps: string[];
  /** What to have on hand. A product is named only where a cited source names it. */
  needs: string[];
  never?: string[];
  why?: string;
  /** Research row ids: "<research file without .md>#<row id>". */
  citations: string[];
  /** Easy steps (docs/EASY.md): one action per screen, lintEasy-clean, 10 at most, as a procedure the apps' step-by-step players take. */
  easy?: EasyProcedure;
}

type Guide = Omit<TaskGuide, "taskKey" | "easy"> & { easy?: { title: string; steps: EasyStep[] } };
const E = (title: string, steps: EasyStep[]) => ({ title, steps });

const TG = (...n: number[]) => n.map((x) => `task-guides#TG${x}`);

// Shared pieces (each re-says the rows it cites)
const DATE_ROWS = TG(1, 2, 3, 4);
const SANITIZER_ROWS = ["sanitizer-selection#S1", "sanitizer-selection#S2", "sanitizer-selection#S4"];


const WORDS: Record<UsState, StateWords<typeof TASKGUIDES_UT>> = perStateTable("TASKGUIDES", TASKGUIDES_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/** Steps a state has no verified text for (null) are left out. */
const steps = (...s: (string | null)[]): string[] => s.filter((x): x is string => x !== null);

const permitGuideFor = (w: StateWords<typeof TASKGUIDES_UT>): Guide => ({
  steps: steps(
    "If the event is open to the public, paid or free, get a county temporary food permit.",
    w.permitDeadlineStep,
    "Each food vendor at the event gets its own permit.",
    "Not sure the event counts as public? Call the health department before you plan.",
    "The person in charge picks up the permit in person, with a food handler card or food safety manager certificate.",
    "Make and store the food at the event or in an approved kitchen, never in a private home.",
  ),
  needs: ["A food handler card or food safety manager certificate for the person in charge"],
  never: ["Never make or store food for the public in a private home."],
  why: "The health department needs to know about the event so it can check the food is safe.",
  citations: TG(75, 76, 77),
  easy: E("Get the event permit", [
    { icon: "phone", say: "Call the county health department about the permit." },
    ...(w.permitDeadlineStep ? [{ icon: "write", say: "Send the form at least one week before the event." } as EasyStep] : []),
    { icon: "check", say: "Bring your food handler card to get the permit." },
    { icon: "stop", say: "Never make event food at home." },
  ]),
});

const trashGuide = (event: boolean): Guide => ({
  steps: [
    ...(event ? ["Clean the floors when the food is put away."] : []),
    "Tie full bags and take them out to the dumpster.",
    "Close the dumpster lid. Outside cans need tight-fitting lids.",
    "Check the drain plugs are in the dumpster and cans.",
    "Wash dirty cans where the water can't reach food, dishes or linens.",
    "Never wash cans or dump the water in a food prep sink, dish sink or hand sink.",
    "Cover indoor cans that hold food waste when you aren't using them, and once they're full.",
    "Keep the trash area clean and clear of anything that doesn't belong there.",
  ],
  needs: [],
  never: ["Never dump can-washing water in a food, dish or hand sink."],
  why: "Open trash and dirty cans draw flies and rodents.",
  citations: [...TG(21, 22, 23, 24, 25, 16), ...(event ? TG(14) : [])],
  easy: E("Take out the trash", [
    { icon: "trash", say: "Tie the full trash bags." },
    { icon: "trash", say: "Take them out to the dumpster." },
    { icon: "trash", say: "Close the dumpster lid." },
    { icon: "water", say: "Wash dirty cans away from food and dishes." },
    { icon: "stop", say: "Never use a food, dish or hand sink for this." },
    { icon: "trash", say: "Put the lids back on the cans." },
  ]),
});

const washGuide: Guide = {
  steps: [
    "Scrape food off the dishes.",
    "Wash in soapy water at least 110°F, or as hot as the soap label says.",
    "Rinse off the soap in a separate tub or sink of clean water.",
    "Sanitize in sanitizer mixed exactly as its label says.",
    "Test the sanitizer with the strip that matches it.",
    "Leave items in the sanitizer for the label's time. The label wins over any shorter time.",
    "Let everything air-dry. Never dry with a towel.",
  ],
  needs: ["Dish soap", "Food-contact sanitizer with an EPA-registered label", "Test strips that match the sanitizer"],
  never: ["Never skip the rinse before sanitizing.", "Never towel-dry sanitized dishes."],
  why: "Wash, rinse, sanitize, dry. Soap left on dishes keeps the sanitizer from working as its label says.",
  citations: [...TG(9, 10, 11, 12), ...SANITIZER_ROWS],
  easy: E("Wash dishes by hand", [
    { icon: "trash", say: "Scrape food off the dishes." },
    { icon: "soap", say: "Wash them in hot soapy water." },
    { icon: "water", say: "Rinse them in clean water." },
    { icon: "chemical", say: "Put them in the sanitizer." },
    { icon: "wait", say: "Leave them in as long as the label says." },
    { icon: "check", say: "Let them dry by themselves." },
  ]),
};

/** The sourced "Show how" guides for a state (Utah's is TASK_GUIDES). */
export function taskGuidesFor(state?: UsState): Record<string, Guide> {
  const w = words(state);
  const permitGuide = permitGuideFor(w);
  const raw: Record<string, Guide> = {
  /* ───────── every shift / opening ───────── */
  "fifo-dates": {
    steps: [
      "Look at every container of ready-to-eat food that must stay cold and was made here.",
      "Find its date. The day it was made counts as Day 1.",
      "Throw it out after 7 days at 41°F or colder.",
      "Throw out anything in a container with no date.",
      "For opened store-bought food, the day you opened it is Day 1. Never go past the maker's use-by date.",
      "Food mixed from dated parts keeps the date of its oldest part.",
      "Home kitchen (MEHK): serve it the same day, or within 72 hours only if you keep the time and temperature logs.",
    ],
    needs: ["A way to mark the date on each container"],
    never: ["Never keep ready-to-eat food with no date on it.", "Never date mixed food later than its oldest part."],
    why: "The Food Code limits ready-to-eat food kept cold to 7 days, counting the day it was made.",
    citations: [...DATE_ROWS, ...TG(5)],
    easy: E("Check the food dates", [
      { icon: "fridge", say: "Check the date on each food container." },
      { icon: "check", say: "Count the day it was made as day one." },
      { icon: "trash", say: "Throw away food older than 7 days." },
      { icon: "trash", say: "Throw away food with no date." },
      { icon: "trash", say: "Throw away food past the maker's use-by date." },
    ]),
  },
  "close-clean": {
    steps: [
      "Scrape or wipe off food.",
      "Wash with soapy water until it is clean to sight and touch.",
      "Rinse off the soap with clean water.",
      "Sanitize with a food-contact sanitizer used exactly as its label says.",
      "Test mixed sanitizer with the test strip that matches it.",
      "Leave it wet for the label's time. The label wins over any shorter time.",
      "Let it air-dry. Never dry it with a towel.",
      "During the day, do this at least every 4 hours, between raw meat types, and when you switch from raw to ready-to-eat food.",
    ],
    needs: ["Dish soap", "Food-contact sanitizer with an EPA-registered label", "Test strips that match the sanitizer"],
    never: ["Never towel-dry a sanitized surface.", "Never skip the rinse before sanitizing."],
    why: "Food-contact surfaces must be clean to sight and touch, then sanitized.",
    citations: [...TG(6, 7, 8, 9, 10, 11), ...SANITIZER_ROWS],
    easy: E("Clean and sanitize a counter", [
      { icon: "box", say: "Take everything off the counter." },
      { icon: "trash", say: "Wipe off crumbs and food." },
      { icon: "soap", say: "Wash it with soapy water." },
      { icon: "water", say: "Wipe it with clean water." },
      { icon: "spray", say: "Spray sanitizer until it is wet.", learnSanitize: "counter_produce" },
      { icon: "wait", say: "Leave it wet as long as the label says." },
      { icon: "check", say: "Let it dry by itself." },
    ]),
  },
  "close-trash": trashGuide(false),
  "close-floors": {
    steps: [
      "Clean floors when the least food is out, such as after closing. Clean spills right away.",
      "Pick up dry dirt without raising dust: a treated dust mop, a vacuum, or a broom with sweeping compound.",
      "Fill the mop bucket, never at a food prep sink, dish sink or hand sink.",
      "Mop the floor.",
      "Dump the dirty water, never in a food prep sink, dish sink or hand sink.",
      "Hang the mop to air-dry where it can't drip on walls, equipment or supplies.",
      "Put the broom, mop and bucket away neatly, where they can't touch food or clean dishes.",
    ],
    needs: ["Mop and bucket", "A treated dust mop, a vacuum, or a broom with sweeping compound"],
    never: ["Never dry-sweep without a sweeping compound.", "Never fill or dump a mop bucket in a food, dish or hand sink."],
    why: "Dust and mop water carry dirt onto food and clean dishes.",
    citations: TG(14, 15, 16, 17, 18),
    easy: E("Mop the floor", [
      { icon: "food", say: "Put food away or cover it first." },
      { icon: "clean", say: "Use a dust mop or vacuum for dry dirt." },
      { icon: "water", say: "Fill the bucket, never at a food or hand sink." },
      { icon: "clean", say: "Mop the floor." },
      { icon: "water", say: "Dump the dirty water, never in a food or hand sink." },
      { icon: "towel", say: "Hang the mop to dry away from food." },
    ]),
  },

  /* ───────── coffee ───────── */
  "coffee-backflush": {
    steps: [
      "Follow your espresso machine maker's cleaning steps first. The steps below are from the Cafiza label.",
      "Put the blind filter in the portafilter and add up to ½ teaspoon (3 g) of Cafiza.",
      "Lock it in. Run the brew cycle 10 seconds, then stop 10 seconds. Do this 5 times.",
      "Take out the portafilter and rinse it in the water running from the group head.",
      "Put it back with no powder and run the same 10 on, 10 off cycle 5 more times.",
      "Put the regular basket back. Make one espresso and throw it out. Don't drink it.",
      "Soak the baskets and the metal part of the portafilter in 6 g of Cafiza per liter of hot water for 30 minutes. Keep the handle out.",
      "Rinse the parts with fresh water.",
      "Clean the shower screen with a brush that doesn't have hard bristles.",
    ],
    needs: ["Espresso machine cleaner, such as Urnex Cafiza", "A blind filter (blank basket)", "A group-head brush without hard bristles"],
    never: ["Never drink the first espresso after a detergent backflush.", "Never soak the portafilter handle.", "Breville: never put machine parts in the dishwasher."],
    why: "The last shot and the plain-water runs rinse the cleaner out before the next drink.",
    citations: [...TG(88, 89), "barista-tools#CL10", "barista-tools#CL15", "barista-tools#M7"],
    easy: E("Backflush the espresso machine", [
      { icon: "wrench", say: "Put the blind basket in the portafilter." },
      { icon: "chemical", say: "Add up to ½ teaspoon of Cafiza." },
      { icon: "power", say: "Run the brew for 10 seconds, then stop.", waitSeconds: 10 },
      { icon: "wait", say: "Wait 10 seconds. Do this 5 times.", waitSeconds: 10 },
      { icon: "water", say: "Rinse the portafilter under the group head." },
      { icon: "power", say: "Do the 5 runs again with no powder." },
      { icon: "trash", say: "Pull one espresso and throw it away." },
      { icon: "chemical", say: "Mix 6 grams of Cafiza in each liter of hot water." },
      { icon: "wait", say: "Soak the baskets for 30 minutes.", waitSeconds: 1800 },
      { icon: "water", say: "Rinse the parts with fresh water." },
    ]),
  },
  "coffee-wand": {
    steps: [
      "Before steaming, purge the wand (a short blast of steam).",
      "Right after steaming, wipe the wand with a clean damp cloth.",
      "Purge it again for 1-2 seconds.",
      "At close, dissolve one 4.0 g Rinza tablet in 250 ml of warm water.",
      "Soak the wand tip in it for 15 to 30 minutes.",
      "Rinse with clean water. Purge before the next use.",
      "If milk stays on the tip, soak it in hot water, or clear the holes with the pin tool if your machine came with one.",
    ],
    needs: ["Steam wand cleaner, such as Urnex Rinza tablets", "A clean damp cloth for the wand"],
    never: ["Never skip the wipe and purge after a milk drink."],
    why: "Milk dries on the wand fast. Cleaning before and after every use keeps it off.",
    citations: [...TG(90), "barista-tools#CL2", "barista-tools#CL3", "barista-tools#M5", "barista-tools#M16"],
    easy: E("Clean the steam wand", [
      { icon: "spray", say: "Let out a short blast of steam first." },
      { icon: "towel", say: "Wipe the wand right after steaming." },
      { icon: "spray", say: "Let out another short blast of steam." },
      { icon: "water", say: "Mix one Rinza tablet in 250 ml warm water." },
      { icon: "wait", say: "Soak the wand tip at least 15 minutes.", waitSeconds: 900 },
      { icon: "water", say: "Rinse it with clean water." },
    ]),
  },
  "coffee-grinder": {
    steps: [
      "Empty all the beans from the hopper and the grinder.",
      "Wash the hopper by hand with water and mild soap. Never in the dishwasher.",
      "Dry the hopper completely before you refill it.",
      "Brush the burrs clean. Never wash burrs with water.",
      "Never use a damp towel inside the grinder.",
      "If you use Grindz: set a medium grind and run 1 jar cap (35-40 g) through an espresso grinder, or 2 caps (70-80 g) through a shop grinder.",
      "Then run coffee beans through the same way to purge it.",
      "Don't use Grindz in a superautomatic machine's built-in grinder.",
    ],
    needs: ["A dry brush", "Mild dish soap", "Grinder cleaning tablets, such as Urnex Grindz (optional)"],
    never: ["Never put water on the burrs.", "Never put the hopper in the dishwasher."],
    why: "Water on burrs causes rust. Old coffee oil builds up in the hopper and burrs.",
    citations: [...TG(91), "barista-tools#G1", "barista-tools#CL30", "barista-tools#CL33"],
    easy: E("Clean the grinder", [
      { icon: "box", say: "Empty the beans from the hopper." },
      { icon: "soap", say: "Wash the hopper by hand with soap." },
      { icon: "stop", say: "Never put the hopper in the dishwasher." },
      { icon: "towel", say: "Dry the hopper all the way." },
      { icon: "clean", say: "Clean the burrs with a dry brush." },
      { icon: "stop", say: "Never get the burrs wet." },
    ]),
  },
  "coffee-descale": {
    steps: [
      "Find your espresso machine maker's descaling instructions and follow them exactly.",
      "If the maker gives you no descaling steps, don't run descaler through the machine. It can clog small parts and harm seals.",
      "Soak only parts that are all metal in descaler, in a separate container.",
      "If the machine already has scale, some makers (ECM) say only the dealer may descale it.",
      "Test your water hardness every 3 months.",
    ],
    needs: ["The descaler your machine maker names"],
    never: ["Never run descaler through the machine unless the maker's instructions say to."],
    why: "Scale is the leading cause of espresso machine failure.",
    citations: ["barista-tools#L7", "barista-tools#CL22", "barista-tools#CL23", "barista-tools#M19", "barista-tools#CL27", "barista-tools#CL1"],
    easy: E("Descale the espresso machine", [
      { icon: "check", say: "Read the machine maker's descaling steps." },
      { icon: "check", say: "Do them exactly as written." },
      { icon: "chemical", say: "Soak only all-metal parts in descaler." },
      { icon: "phone", say: "Call the dealer if the machine already has scale." },
      { icon: "check", say: "Test the water hardness every 3 months." },
    ]),
  },

  /* ───────── cleaning ───────── */
  "restroom-clean": {
    steps: [
      "Clean the toilets, urinals and sinks until they are clean.",
      "Use toilet bowl cleaner or disinfectant exactly as its label says.",
      "Leave it wet as long as the label says. Clorox Toilet Bowl Cleaner with Bleach: 5 minutes to sanitize, 10 to disinfect.",
      "Keep children and pets away from the toilet while it works.",
      "Refill the soap at every hand sink.",
      "Refill the paper towels, or check the hand dryer works.",
      "Put toilet paper at every toilet.",
      "Check the women's restroom has a covered bin for sanitary napkins.",
      "Check the handwashing sign is up.",
      "Never store food in the restroom.",
    ],
    needs: ["Toilet bowl cleaner or disinfectant, such as Clorox Toilet Bowl Cleaner with Bleach", "Hand soap", "Paper towels", "Toilet paper"],
    never: ["Never mix bleach with ammonia or acid cleaners.", "Never store food in a restroom."],
    why: "A stocked sink lets everyone wash their hands.",
    citations: [...TG(26, 27, 28, 29, 30, 19), "sanitizer-selection#P8", "sanitizer-selection#S21", "sanitizer-selection#S19", "hygiene-and-kits#F13"],
    easy: E("Clean the restroom", [
      { icon: "restroom", say: "Clean the toilet, urinal and sink." },
      { icon: "spray", say: "Put in toilet cleaner as the label says." },
      { icon: "wait", say: "Leave it as long as the label says." },
      { icon: "people", say: "Keep kids and pets away while it works." },
      { icon: "soap", say: "Refill the soap." },
      { icon: "towel", say: "Refill the paper towels." },
      { icon: "restroom", say: "Put toilet paper by every toilet." },
      { icon: "stop", say: "Never keep food in the restroom." },
    ]),
  },
  "tool-separation": {
    steps: [
      "Never fill or dump mop buckets, or wash cleaning tools, in a food prep sink, dish sink or hand sink.",
      "Hang mops so they air-dry without touching walls, equipment or supplies.",
      "Store brooms, mops and buckets neatly, where they can't touch food, clean dishes or linens.",
      "Keep chemicals apart from food, and never above food, dishes or linens.",
      "Keep food out of the restroom, locker room, garbage room and mechanical room.",
    ],
    needs: [],
    never: ["Never dump mop water in a food, dish or hand sink.", "Never store food in a restroom."],
    why: "Mops, buckets and chemicals carry dirt and poison onto food if they are kept near it.",
    citations: TG(16, 17, 18, 19, 20),
    easy: E("Keep mops away from food", [
      { icon: "stop", say: "Never fill a mop bucket at a food or hand sink." },
      { icon: "stop", say: "Never dump mop water in a food or hand sink." },
      { icon: "towel", say: "Hang the mop up to dry." },
      { icon: "box", say: "Store mops and brooms away from food." },
      { icon: "chemical", say: "Store chemicals below and away from food." },
      { icon: "restroom", say: "Keep food out of the restroom." },
    ]),
  },
  "cloths-laundry": {
    steps: [
      "Keep wet wiping cloths in sanitizer between uses.",
      "Mix the sanitizer as its label says and test it with the matching strip. Quat labels such as Steramine and Multi-Quat read 200 to 400 ppm.",
      "Change the bucket when you see food bits or dirt in it.",
      "Keep the bucket off the floor and away from food.",
      "Keep cloths used on raw meat surfaces apart from other cloths.",
      "Keep cloths for wiping spills off plates dry, and use them for nothing else.",
      "Send wet cloths to the laundry every day.",
      "Put dirty cloths in a clean, nonabsorbent bin or a washable laundry bag.",
    ],
    needs: ["Food-contact sanitizer with an EPA-registered label", "Test strips that match the sanitizer", "A bucket kept off the floor"],
    never: ["Never leave a wet cloth lying on the counter between uses.", "Never use raw meat cloths for anything else."],
    why: "A cloth sitting in weak or dirty sanitizer spreads germs instead of killing them.",
    citations: [...TG(31, 32, 33, 34), "sanitizer-selection#S2", "sanitizer-selection#S4", "sanitizer-selection#S26"],
    easy: E("Take care of wiping cloths", [
      { icon: "towel", say: "Keep wet cloths in the sanitizer bucket." },
      { icon: "check", say: "Test the bucket with the right strip." },
      { icon: "water", say: "Change it when it has food bits or dirt." },
      { icon: "box", say: "Keep the bucket off the floor." },
      { icon: "towel", say: "Keep raw meat cloths apart from other cloths." },
      { icon: "towel", say: "Send wet cloths to the laundry every day." },
    ]),
  },
  "fridge-deep": {
    steps: [
      "Move the food to another fridge or a cooler that holds 41°F or colder.",
      "Throw out food that is past its date or has no date.",
      "Wash the shelves, drawers and walls with mild detergent in warm water.",
      "Rinse and dry the parts you took out.",
      "Never wash cold glass shelves with warm water. Let them warm up first.",
      "Don't use solvent cleaners or scouring powders inside the fridge.",
      "Let the fridge get cold again before the food goes back in.",
      "Put the food back in covered containers or wrap.",
    ],
    needs: ["Mild detergent", "A cooler or another fridge that holds 41°F or colder"],
    never: ["Never put warm water on cold glass shelves.", "Never use solvent cleaners or abrasives inside a fridge."],
    why: "Spills and old food inside the fridge build up if nobody empties it and cleans.",
    citations: [...TG(35, 4, 38), "home-cleaning-appliances#K6", "kitchenaid-appliances#R4", "kitchenaid-appliances#R5", "hygiene-and-kits#F15"],
    easy: E("Deep clean the fridge", [
      { icon: "fridge", say: "Move the food to a cold cooler or fridge." },
      { icon: "trash", say: "Throw away food past its date or with no date." },
      { icon: "soap", say: "Wash shelves and walls with warm soapy water." },
      { icon: "stop", say: "Never put warm water on cold glass shelves." },
      { icon: "water", say: "Rinse and dry the parts." },
      { icon: "wait", say: "Let the fridge get cold again." },
      { icon: "fridge", say: "Put the food back covered." },
    ]),
  },
  "fridge-order": {
    steps: [
      "Put raw meat, fish and poultry below cooked and ready-to-eat food.",
      "Keep raw meat away from fruit and vegetables that aren't washed yet.",
      "Stack raw meats by cooking temperature: fish and pork (145°F) above raw poultry (165°F).",
      "Keep raw poultry on the bottom shelf.",
      "Keep each raw meat type apart, in its own container or with space between.",
      "Cover or wrap all food.",
    ],
    needs: [],
    never: ["Never store raw meat above food that won't be cooked."],
    why: "Juice from raw meat can drip onto food below it. The meat that needs the hottest cooking goes lowest.",
    citations: [...TG(36, 37, 38), "home-storage-organization#H23"],
    easy: E("Put fridge food in order", [
      { icon: "fridge", say: "Put cooked food on the top shelves." },
      { icon: "fridge", say: "Put raw meat below all other food." },
      { icon: "fridge", say: "Put raw chicken on the bottom shelf." },
      { icon: "box", say: "Keep each kind of raw meat in its own container." },
      { icon: "box", say: "Cover or wrap all food." },
    ]),
  },

  /* ───────── maintenance ───────── */
  "hood-pro": {
    steps: [
      "Have a hood cleaning company clean the hood at regular intervals.",
      "Keep the ducts cleaned and filters changed so they don't drop dust and dirt.",
      "Keep the grease filters in place in the hood.",
      "Keep the hood fire suppression system tagged and serviced every 6 months.",
    ],
    needs: ["A hood cleaning company"],
    citations: TG(49, 54),
    easy: E("Get the hood cleaned", [
      { icon: "phone", say: "Call the hood cleaning company when it is due." },
      { icon: "check", say: "Check the grease filters are in the hood." },
      { icon: "check", say: "Check the fire system tag is current." },
    ]),
  },
  fire: {
    steps: [
      "Look over every portable fire extinguisher each month.",
      "Check each one is in its set place, fully charged and working.",
      "Have a fire protection company do each extinguisher's yearly maintenance check.",
      "Write down the yearly maintenance date and keep the record at least one year.",
      "Have the hood fire suppression system inspected every year by someone who knows the system.",
      "Keep the hood system tagged and serviced every 6 months (fire code).",
    ],
    needs: ["A fire protection company"],
    why: "An extinguisher or hood system that isn't checked may not work in a fire.",
    citations: TG(45, 46, 47, 48, 49),
    easy: E("Check the fire extinguishers", [
      { icon: "check", say: "Look at each fire extinguisher every month." },
      { icon: "check", say: "Check it is in its place and charged." },
      { icon: "phone", say: "Call the fire company for the yearly check." },
      { icon: "write", say: "Write down the yearly check date." },
      { icon: "check", say: "Check the hood fire system tag." },
    ]),
  },
  "backflow-test": {
    steps: [
      "Have a certified backflow tester inspect and test each backflow assembly at least once a year.",
      "Also have it serviced when its maker's instructions say to.",
      "Keep the test and service records. The person in charge holds them.",
      "Ask your water system whether you or they are responsible for the test.",
    ],
    needs: ["A certified backflow tester"],
    why: "A failed backflow device can let dirty water flow back into the drinking water.",
    citations: TG(43, 44),
    easy: E("Get the backflow test", [
      { icon: "phone", say: "Call a certified backflow tester every year." },
      { icon: "write", say: "Keep the test report." },
    ]),
  },
  "pest-service": {
    steps: [
      "Let only a certified pest control operator put out pesticide, following its label for food businesses.",
      "Make sure rodent bait is only inside covered, tamper-resistant bait stations.",
      "Check deliveries for pests before you put them away.",
      "Walk the building and look for signs of pests.",
      "Take out dead or trapped pests often, so they don't pile up.",
      "Close up places pests could hide.",
    ],
    needs: ["A certified pest control operator"],
    never: ["Never use a pesticide whose label doesn't allow use in a food business."],
    why: "Pests carry germs onto food and supplies.",
    citations: TG(39, 40, 41, 42),
    easy: E("Keep pests out", [
      { icon: "check", say: "Check deliveries for pests." },
      { icon: "bug", say: "Look for signs of pests around the building." },
      { icon: "trash", say: "Throw away dead or trapped pests." },
      { icon: "bug", say: "Keep rodent bait only in closed bait boxes." },
      { icon: "stop", say: "Never spray pesticide yourself." },
    ]),
  },
  "sds-firstaid": {
    steps: [
      "Check there is a safety data sheet for every chemical you use.",
      "Keep the sheets where staff can reach them on every shift. Electronic copies are fine if nothing blocks access.",
      "Check the first aid kit is stocked and easy to reach.",
      "Check the kit is labeled and kept where it can't drip on food or equipment.",
      "Keep single-use gloves with the bandages: a bandage on the hand is covered with a glove.",
      "Check someone on staff is trained in first aid if no clinic is close by.",
    ],
    needs: ["A labeled first aid kit", "Single-use gloves"],
    citations: [...TG(55, 56, 57), "hygiene-and-kits#F11"],
    easy: E("Check safety sheets and first aid", [
      { icon: "chemical", say: "Check each chemical has a safety sheet." },
      { icon: "box", say: "Keep the sheets where everyone can reach them." },
      { icon: "bandage", say: "Check the first aid kit is full." },
      { icon: "gloves", say: "Keep gloves with the bandages." },
    ]),
  },

  /* ───────── menu and self-service ───────── */
  "menu-advisory": {
    steps: [
      "Find every animal food you serve raw, undercooked, or cooked to order, like burgers to order, soft eggs or rare fish.",
      "Describe it on the menu, like \"hamburgers (can be cooked to order)\", or put a star by it.",
      "Star it to a footnote, such as: \"Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs may increase your risk of foodborne illness.\"",
      "Use the menu, a table tent, a placard or a brochure.",
      "A whole, intact beef steak seared on top and bottom to 145°F with a color change doesn't need the star (not for a highly susceptible group).",
    ],
    needs: [],
    citations: TG(58, 59, 60, 61),
    easy: E("Mark raw foods on the menu", [
      { icon: "check", say: "Find foods served raw or cooked to order." },
      { icon: "write", say: "Put a star by each one on the menu." },
      { icon: "write", say: "Add the risk note at the bottom." },
    ]),
  },
  "buffet-clean-plate-sign": {
    steps: [
      "Put up a sign telling guests to take a clean plate each trip.",
      "Never let guests get more food with a used plate.",
      "Check sneeze guards or covers protect all the food.",
      "Put a serving utensil in every dish, handle up.",
      "Have a staff member trained in safe buffet steps watch the line.",
      "Never put food a guest took back onto the line.",
    ],
    needs: ["A clean-plate sign", "A serving utensil for every dish"],
    citations: [...TG(62, 63, 64, 65, 66), "hygiene-and-kits#F25"],
    easy: E("Set up the buffet safely", [
      { icon: "sign", say: "Put up the clean plate sign." },
      { icon: "check", say: "Check the sneeze guards are in place." },
      { icon: "food", say: "Put a spoon or tongs in every dish." },
      { icon: "people", say: "Stand by the line and watch it." },
      { icon: "stop", say: "Never let guests reuse a dirty plate." },
    ]),
  },

  /* ───────── food trucks ───────── */
  "mfb-permit-renew": {
    steps: [
      "Renew the truck health permit within 30 days before it expires.",
      "Meet the same application requirements as when you first applied.",
      "Post the new permit inside the truck where it can be seen.",
      "Put the new decal on the outside rear of the truck. Carts: both on the cart.",
    ],
    needs: [],
    citations: TG(68, 69),
    easy: E("Renew the truck permit", [
      { icon: "write", say: "Send the permit renewal within 30 days before it expires." },
      { icon: "sign", say: "Put the permit up inside the truck." },
      { icon: "sign", say: "Put the decal on the back outside." },
    ]),
  },
  "mfb-commissary-renew": {
    steps: [
      "Renew the signed commissary agreement every year.",
      "Tell the health department about any change to the agreement before it happens.",
    ],
    needs: [],
    citations: TG(70),
    easy: E("Renew the commissary agreement", [
      { icon: "write", say: "Sign a new commissary agreement every year." },
      { icon: "phone", say: "Tell the health department before anything changes." },
    ]),
  },
  "mfb-fire-inspection": {
    steps: [
      "Book the yearly fire inspection with your local fire department.",
      "Check the listed propane detector is at floor level near the cooking equipment.",
      "Keep propane cylinders outside the vehicle, closed when not in use, and the system leak-free.",
      "Never refuel or swap tanks during an event or with the public near.",
      "Secure gas appliances to the truck. No customers inside.",
      "Have a 2-A:10-B:C extinguisher, and a Class K one if you have a hood.",
      "Put the new sticker inside the rear-most door. It is good for one year.",
      "Post a No Smoking sign.",
    ],
    needs: ["A listed propane (LPG) gas detector", "A 2-A:10-B:C extinguisher", "A Class K extinguisher if the truck has a hood", "A No Smoking sign"],
    never: ["Never carry or store propane cylinders inside the truck.", "Never refuel propane with the public present."],
    citations: TG(50, 51, 52, 53),
    easy: E("Get the truck fire inspection", [
      { icon: "phone", say: "Call the fire department to book the inspection." },
      { icon: "check", say: "Check the propane detector is near the floor." },
      { icon: "stop", say: "Never fill propane with the public near." },
      { icon: "sign", say: "Put the sticker inside the back door." },
    ]),
  },
  "mfb-hood-suppression": {
    steps: [
      "Have a fire protection company service the hood suppression system every 6 months.",
      "Check it carries a current service tag.",
      "Have the hood cleaned at regular intervals.",
      "Keep the grease filters in place.",
    ],
    needs: ["A fire protection company"],
    citations: TG(49, 48),
    easy: E("Service the hood fire system", [
      { icon: "phone", say: "Call the fire company every 6 months." },
      { icon: "check", say: "Check the hood system tag is current." },
    ]),
  },
  "mfb-water": {
    steps: [
      "Fill the fresh water tank only with a hose made for drinking water.",
      "Keep caps on the inlet, outlet and hose when not in use.",
      "Store fresh-water hoses apart from waste hoses.",
      "Dump waste water only at the commissary, an approved servicing area, or into a waste water truck.",
      "Flush and sanitize the tank, pump and hoses when new, after repairs, and after 30 days or more unused.",
    ],
    needs: ["A drinking-water hose", "Caps for the inlet, outlet and hose"],
    never: ["Never fill the fresh tank with a hose used for anything else."],
    citations: TG(71, 72, 73),
    easy: E("Fill and dump truck water", [
      { icon: "water", say: "Fill the tank with the drinking water hose only." },
      { icon: "check", say: "Put the caps back on." },
      { icon: "box", say: "Keep the fresh hose away from the waste hose." },
      { icon: "water", say: "Dump waste water only at the commissary or approved site." },
    ]),
  },
  "mfb-documents": {
    steps: [
      "Keep a copy of the business license on the truck.",
      "Keep the health permit posted inside the truck.",
      "Keep proof of the fire inspection. The sticker goes inside the rear-most door.",
    ],
    needs: [],
    citations: TG(74, 69, 53),
    easy: E("Check the truck papers", [
      { icon: "check", say: "Check the business license is on the truck." },
      { icon: "check", say: "Check the health permit is up inside." },
      { icon: "check", say: "Check the fire sticker is inside the back door." },
    ]),
  },

  /* ───────── events ───────── */
  "event-permit": permitGuide,
  permit: permitGuide,
  pic: {
    steps: steps(
      "Name one person in charge for the event.",
      "Make sure a person in charge is there the whole time food is served.",
      w.boothCardStep,
    ),
    needs: [],
    citations: TG(78, 77),
    easy: E("Pick the person in charge", [
      { icon: "people", say: "Pick one person to be in charge." },
      { icon: "people", say: "Keep the person in charge there the whole time." },
    ]),
  },
  cleared: {
    steps: steps(
      w.workerPermitStep,
      "Check each worker knows to tell the person in charge about vomiting, diarrhea, jaundice, sore throat with fever, or a pus-filled sore.",
    ),
    needs: [],
    citations: TG(79, 77, 80),
    easy: E("Check food handler cards", [
      { icon: "check", say: "Check each food worker has a food handler card." },
      { icon: "sick", say: "Tell workers to report being sick." },
    ]),
  },
  sources: {
    steps: [
      "Buy food only from sources that follow the law, such as licensed stores and suppliers.",
      "Never serve food made in a private home.",
      "Thaw frozen meat in the fridge at 41°F or colder.",
    ],
    needs: [],
    never: ["Never serve food made in a private home."],
    citations: TG(81, 82),
    easy: E("Buy food from safe places", [
      { icon: "food", say: "Get food only from licensed stores." },
      { icon: "stop", say: "Never serve food made at home." },
      { icon: "fridge", say: "Thaw meat in the fridge." },
    ]),
  },
  gear: {
    steps: [
      "Check each food thermometer the way its maker says, so it reads true.",
      "Bring a thin-tip probe for thin food like burger patties.",
      "Bring test strips that match your sanitizer.",
      "Bring enough single-use gloves to change them for every task.",
    ],
    needs: ["Food thermometer with a thin probe", "Sanitizer test strips", "Single-use gloves"],
    citations: [...TG(83, 84), "sanitizer-selection#S4", "hygiene-and-kits#F18"],
    easy: E("Pack the food safety gear", [
      { icon: "thermo", say: "Check each thermometer as its maker says." },
      { icon: "check", say: "Bring test strips for your sanitizer." },
      { icon: "gloves", say: "Bring plenty of gloves." },
    ]),
  },
  health: {
    steps: [
      "Ask each food worker about vomiting, diarrhea, jaundice, sore throat with fever, and open or draining pus-filled sores.",
      "Anyone with one of these tells the person in charge before handling food.",
    ],
    needs: [],
    citations: TG(80),
    easy: E("Ask about sickness", [
      { icon: "sick", say: "Ask about throwing up or diarrhea." },
      { icon: "sick", say: "Ask about a sore throat with fever." },
      { icon: "bandage", say: "Ask about open sores with pus." },
      { icon: "phone", say: "Tell the person in charge if anyone says yes.", callManager: true },
    ]),
  },
  handwash: {
    steps: [
      "Set up a handwashing station with running warm water.",
      "Put out soap.",
      "Put out paper towels.",
      "Put up a handwashing sign.",
      "Put a trash can by the sink for used towels.",
      "Wash hands: warm water, soap, rub 10 to 15 seconds, rinse, dry. At least 20 seconds in all.",
    ],
    needs: ["Soap", "Paper towels", "A trash can", "A handwashing sign"],
    citations: [...TG(87, 27, 28), "hygiene-and-kits#F13"],
    easy: E("Set up hand washing", [
      { icon: "water", say: "Set up running warm water for hand washing." },
      { icon: "soap", say: "Put out soap." },
      { icon: "towel", say: "Put out paper towels." },
      { icon: "trash", say: "Put a trash can by the sink." },
      { icon: "sign", say: "Put up the wash your hands sign." },
    ]),
  },
  tubs: {
    steps: [
      "Set up three tubs: wash, rinse, sanitize.",
      "Fill the wash tub with soapy water at least 110°F, or as hot as the soap label says.",
      "Fill the rinse tub with clean water.",
      "Mix the sanitizer tub exactly as its label says.",
      "Test the sanitizer with the matching strip.",
      "Wash, rinse, then leave items in the sanitizer for the label's time.",
      "Let items air-dry.",
    ],
    needs: ["Three tubs", "Dish soap", "Food-contact sanitizer with an EPA-registered label", "Test strips that match the sanitizer"],
    citations: [...TG(9, 10, 11, 12), ...SANITIZER_ROWS],
    easy: E("Set up the dish tubs", [
      { icon: "soap", say: "Fill the first tub with hot soapy water." },
      { icon: "water", say: "Fill the second tub with clean water." },
      { icon: "chemical", say: "Mix the third tub with sanitizer as the label says." },
      { icon: "check", say: "Test the sanitizer with a strip." },
    ]),
  },
  coolers: {
    steps: [
      "Keep cold food at 41°F or colder.",
      "Pack drinks in one cooler and perishable food in another.",
      "Put raw meat in waterproof containers.",
      "Keep coolers in the shade and open them as little as you can.",
    ],
    needs: ["Separate coolers for drinks and food", "Waterproof containers for raw meat", "Ice or ice packs"],
    citations: ["hygiene-and-kits#F15", "hygiene-and-kits#U2", "hygiene-and-kits#U3"],
    easy: E("Pack the coolers", [
      { icon: "fridge", say: "Keep cold food at 41°F or colder." },
      { icon: "box", say: "Put drinks in a different cooler than food." },
      { icon: "box", say: "Put raw meat in waterproof containers." },
    ]),
  },
  allergens: {
    steps: [
      "Tell guests in writing which major allergens are in each unpackaged food.",
      "Put an allergen card by every dish.",
    ],
    needs: ["Allergen cards"],
    citations: TG(67),
    easy: E("Put out allergen cards", [{ icon: "allergy", say: "Put an allergen card by each dish." }]),
  },
  probe: {
    steps: [
      "Check every batch with a food thermometer. It's the only way to know it's cooked.",
      "Poultry: 165°F or above.",
      "Burgers and other ground meat: 155°F for 17 seconds.",
      "Whole cuts of meat, fish, and eggs cooked to order: 145°F for 15 seconds.",
      "Keep cooking anything that hasn't reached its temperature.",
    ],
    needs: ["Food thermometer"],
    citations: [...TG(85), "hygiene-and-kits#C10"],
    easy: E("Check cooking temperatures", [
      { icon: "thermo", say: "Check each batch with a food thermometer." },
      { icon: "thermo", say: "Cook chicken to 165°F or hotter." },
      { icon: "thermo", say: "Cook burgers to 155°F for 17 seconds." },
      { icon: "food", say: "Keep cooking if it is not hot enough." },
    ]),
  },
  holding: {
    steps: [
      "Keep hot food at 135°F or above.",
      "Keep cold food at 41°F or below.",
      "Check them with a food thermometer.",
    ],
    needs: ["Food thermometer"],
    citations: ["hygiene-and-kits#F15"],
    easy: E("Keep food hot or cold", [
      { icon: "thermo", say: "Keep hot food at 135°F or hotter." },
      { icon: "fridge", say: "Keep cold food at 41°F or colder." },
    ]),
  },
  fourhour: {
    steps: [
      "Use time instead of temperature only if you wrote down the procedure ahead of time.",
      "Start with food at 41°F or colder, or 135°F or hotter.",
      "Mark the time 4 hours from when it left the fridge or the heat.",
      "Serve it or throw it out within 4 hours.",
      "Throw out any food with no time mark.",
    ],
    needs: ["A marker or labels for the time"],
    citations: TG(86),
    easy: E("Use the 4-hour rule", [
      { icon: "write", say: "Write the time 4 hours from now on it." },
      { icon: "trash", say: "Throw it away when that time comes." },
      { icon: "trash", say: "Throw away food with no time mark." },
    ]),
  },
  discard: {
    steps: [
      "Throw out food held on time when its 4 hours are up.",
      "Never serve again food a guest had or sent back. The only exceptions: a closed narrow-neck bottle, or crackers, salt or pepper in an unopened original package in sound condition, and only for food that does not need to stay hot or cold.",
    ],
    needs: [],
    citations: TG(86, 66),
    easy: E("Throw away old food", [
      { icon: "trash", say: "Throw away food when its time is up." },
      { icon: "stop", say: "Never serve food a guest sent back." },
      { icon: "check", say: "Put back only a closed bottle or an unopened package." },
      { icon: "thermo", say: "Check first: it must need no hot or cold." },
    ]),
  },
  wash: washGuide,
  trash: trashGuide(true),
  };
  // Warnings first (easy.ts warningsFirst): a never / don't line is read before the step it warns about, not after.
  return Object.fromEntries(Object.entries(raw).map(([k, g]) => [k, g.easy ? { ...g, easy: { ...g.easy, steps: warningsFirst(g.easy.steps) } } : g]));
}

export const TASK_GUIDES: Record<string, Guide> = taskGuidesFor("UT");
const GUIDES_BY_STATE = new Map<UsState, Record<string, Guide>>();

/** Tasks with no sourced guide yet, and the research that would fill each (docs: task-guides.md "Not sourced yet"). */
export const TASK_GUIDES_NOT_SOURCED: { taskKey: string; needs: string }[] = [
  { taskKey: "drains", needs: "Drain or floor-sink maker's care page, or an extension or Cleaning Institute page on floor drains and drain flies." },
  { taskKey: "hood-filters", needs: "Hood or baffle-filter maker's cleaning instructions (soak product, dilution, time) or the degreaser label." },
  { taskKey: "grease-trap", needs: "The local sewer district's FOG (grease) pretreatment rules for pump-out interval and manifest." },
];

/** The sourced "Show how" guide for a starter task key (tasks.ts TASK_SEEDS or EVENT_CHECKLIST; "event:" prefix ok). */
export function taskGuideFor(taskKey: string, state?: UsState): TaskGuide | undefined {
  const key = taskKey.replace(/^event:/, "");
  let table = TASK_GUIDES;
  if (state && state !== "UT") {
    table = GUIDES_BY_STATE.get(state) ?? taskGuidesFor(state);
    GUIDES_BY_STATE.set(state, table);
  }
  const g = table[key];
  if (!g) return undefined;
  const { easy, ...rest } = g;
  return {
    taskKey: key, ...rest,
    easy: easy && { id: `task-${key}`, title: easy.title, why: g.why ?? easy.title, for: {}, steps: easy.steps, citations: g.citations },
  };
}
